/**
 * Custom hook: Entry / waiter / soft-delete restore actions.
 */

import axios from 'axios';
import { API } from '../utils/constants';
import { fmtPLN } from '../utils/helpers';

export const useEntriesActions = ({
  user,
  location,
  // Entry form state
  tipDate,
  selectedWaiter, setSelectedWaiter,
  tipsAmount, setTipsAmount,
  notes, setNotes,
  // Waiter form state
  firstName, setFirstName,
  lastName, setLastName,
  branch, setBranch,
  birthDate, setBirthDate,
  waiterStatus,
  workerCategory, setWorkerCategory,
  // Day context
  isDayClosed,
  dayEntries,
  dayTotals,
  generateThermalPDF,
  refresh,
}) => {
  const handleSaveEntry = async () => {
    if (isDayClosed(tipDate)) {
      alert('Ten dzień jest zamknięty.');
      return;
    }
    if (!selectedWaiter) {
      alert('Wybierz kelnera.');
      return;
    }
    const amount = Number(tipsAmount);
    if (!Number.isFinite(amount) || amount < 0) {
      alert('Podaj poprawną kwotę.');
      return;
    }
    try {
      await axios.post(`${API}/entries`, {
        date: tipDate,
        waiterId: selectedWaiter,
        manager: user.username,
        amount,
        notes: notes.trim(),
        location,
      });
      setTipsAmount('');
      setNotes('');
      setSelectedWaiter('');
      refresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Błąd zapisu.');
    }
  };

  const handleCloseDay = async () => {
    if (isDayClosed(tipDate)) {
      alert('Ten dzień jest już zamknięty.');
      return;
    }
    if (dayEntries.length === 0) {
      alert('Nie można zamknąć dnia bez wpisów.');
      return;
    }
    const confirmMsg =
      location === 'krakow'
        ? `Zamknąć dzień ${tipDate}?\n\nSuma napiwków: ${fmtPLN(dayTotals.amount)} zł\nKelnerzy (60%): ${fmtPLN(dayTotals.waiter)} zł\nKuchnia (30%): ${fmtPLN(dayTotals.kitchen)} zł\nBar (10%): ${fmtPLN(dayTotals.bar)} zł`
        : `Zamknąć dzień ${tipDate}?\n\nSuma napiwków: ${fmtPLN(dayTotals.amount)} zł\nKelnerzy (60%): ${fmtPLN(dayTotals.waiter)} zł\nKuchnia (40%): ${fmtPLN(dayTotals.kitchen)} zł`;
    if (!await window.confirm(confirmMsg)) return;
    try {
      await axios.post(`${API}/closed-days`, { date: tipDate, closedBy: user.username, location });
      await generateThermalPDF(tipDate);
      refresh();
      alert('Dzień zamknięty!');
    } catch (e) {
      alert(e.response?.data?.detail || 'Błąd zamykania dnia.');
    }
  };

  const handleOpenDay = async () => {
    if (!isDayClosed(tipDate)) return;
    if (!await window.confirm(`Odblokować dzień ${tipDate}?`)) return;
    try {
      await axios.delete(`${API}/closed-days/${tipDate}?location=${location}`);
      refresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Błąd.');
    }
  };

  const handleDeleteEntry = async (entryId, entryDate) => {
    if (isDayClosed(entryDate)) {
      alert('Nie można usuwać z zamkniętego dnia.');
      return;
    }
    if (!await window.confirm('Usunąć wpis?')) return;
    try {
      await axios.delete(`${API}/entries/${entryId}`);
      refresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Błąd.');
    }
  };

  const handleRestoreEntry = async (entryId) => {
    if (!await window.confirm('Przywrócić ten wpis?')) return;
    try {
      await axios.post(`${API}/entries/${entryId}/restore`);
      refresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Błąd.');
    }
  };

  const handleRestoreWaiter = async (waiterId) => {
    if (!await window.confirm('Przywrócić tego kelnera?')) return;
    try {
      await axios.post(`${API}/waiters/${waiterId}/restore`);
      refresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Błąd.');
    }
  };

  const handleRestoreBillard = async (entryId) => {
    if (!await window.confirm('Przywrócić ten wpis billard?')) return;
    try {
      await axios.post(`${API}/billard/${entryId}/restore`);
      refresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Błąd.');
    }
  };

  const handleRestoreClothing = async (itemId) => {
    if (!await window.confirm('Przywrócić ten przedmiot?')) return;
    try {
      await axios.post(`${API}/clothing/${itemId}/restore`);
      refresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Błąd.');
    }
  };

  const handleAddWaiter = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      alert('Podaj imię i nazwisko.');
      return;
    }
    try {
      await axios.post(`${API}/waiters`, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        branch: branch.trim(),
        birthDate: birthDate || null,
        status: waiterStatus,
        category: workerCategory,
        location,
      });
      setFirstName('');
      setLastName('');
      setBranch('');
      setBirthDate('');
      setWorkerCategory('kelnerzy');
      refresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Błąd.');
    }
  };

  const handleSetWaiterStatus = async (waiterId, status) => {
    try {
      await axios.put(`${API}/waiters/${waiterId}/status`, { status });
      refresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Błąd.');
    }
  };

  return {
    handleSaveEntry,
    handleCloseDay,
    handleOpenDay,
    handleDeleteEntry,
    handleRestoreEntry,
    handleRestoreWaiter,
    handleRestoreBillard,
    handleRestoreClothing,
    handleAddWaiter,
    handleSetWaiterStatus,
  };
};
